import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { fmtDateTime, initials as initialsOf } from 'utilities/format';
import { StatusChip, GlassIconButton } from 'dashboard/components/common';

const STATUS_COLORS = { active: 'success', invited: 'info', blocked: 'error' };

const initialsFor = (c) => initialsOf(c?.display_name || c?.contact_email || '');

/**
 * Brand-side customer drawer. Shows the projected list row plus the
 * fetched recent_orders + addresses, then lets the owner edit status,
 * tags, and a private note on this brand.
 */
export default function CustomerDialog({ open, onClose, brandId, row, onSaved }) {
  const [editing, setEditing] = useState(null);
  const [tagDraft, setTagDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (open && row) setEditing({
      status: row.status || 'active',
      tags: row.tags || [],
      note_internal: row.note_internal || '',
    });
  }, [open, row]);

  useEffect(() => {
    if (!open || !row?._id || !brandId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    API.getBrandCustomer({ brand_id: brandId, _id: row._id })
      .then((res) => setDetail(res || null))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [open, row?._id, brandId]);

  const addTag = () => {
    const t = tagDraft.trim();
    if (!t || !editing) return;
    if (editing.tags.includes(t)) return;
    setEditing({ ...editing, tags: [...editing.tags, t] });
    setTagDraft('');
  };
  const rmTag = (t) => setEditing({ ...editing, tags: editing.tags.filter((x) => x !== t) });

  const save = () => {
    setSaving(true);
    API.updateBrandCustomer({ brand_id: brandId, _id: row._id, patch: editing })
      .then(() => { toast.success('Saved'); onSaved && onSaved(); onClose(); })
      .catch((err) => toast.error(err?.message || 'Failed'))
      .finally(() => setSaving(false));
  };

  if (!row || !editing) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth scroll='paper'>
      <DialogTitle sx={{ pr: '48px' }}>
        <Stack direction='row' spacing='10px' alignItems='center' flexWrap='wrap'>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 13 }}>
            {initialsFor(row)}
          </Avatar>
          <Box>
            <Typography variant='body1' fontWeight='700'>{row.display_name || '—'}</Typography>
            <Typography variant='caption' color='text.secondary'>{row.contact_email}</Typography>
          </Box>
        </Stack>
        <GlassIconButton label='Close'
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size='small'
        >
          <CloseIcon fontSize='small' />
        </GlassIconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing='14px'>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='12px'>
            <Box bgcolor='action.hover' p='10px' borderRadius='10px' flex='1'>
              <Typography variant='caption' color='text.secondary'>Orders</Typography>
              <Typography variant='h6' fontWeight='700'>{row.order_count || 0}</Typography>
            </Box>
            <Box bgcolor='action.hover' p='10px' borderRadius='10px' flex='1'>
              <Typography variant='caption' color='text.secondary'>Lifetime value</Typography>
              <Typography variant='h6' fontWeight='700'>
                {Number(row.lifetime_value || 0).toFixed(2)}
              </Typography>
            </Box>
            <Box bgcolor='action.hover' p='10px' borderRadius='10px' flex='1'>
              <Typography variant='caption' color='text.secondary'>Last signed in</Typography>
              <Typography variant='body2' fontWeight='700'>{fmtDateTime(row.last_login_at)}</Typography>
            </Box>
          </Stack>

          <Box>
            <Typography variant='body2' fontWeight='700' mb='6px'>Recent orders</Typography>
            <Box border='1px solid' borderColor='divider' borderRadius='10px' overflow='auto'>
              <Box component='table' width='100%' fontSize='13px'>
                <Box component='thead' bgcolor='action.hover'>
                  <Box component='tr'>
                    <Box component='th' textAlign='left' p='8px 10px'>Order</Box>
                    <Box component='th' textAlign='left' p='8px 10px'>Status</Box>
                    <Box component='th' textAlign='right' p='8px 10px'>Items</Box>
                    <Box component='th' textAlign='right' p='8px 10px'>Total</Box>
                  </Box>
                </Box>
                <Box component='tbody'>
                  {detailLoading && !detail && (
                    <Box component='tr'>
                      <Box component='td' p='8px 10px' colSpan={4}>
                        <Typography variant='caption' color='text.secondary'>Loading…</Typography>
                      </Box>
                    </Box>
                  )}
                  {!detailLoading && (detail?.recent_orders || []).length === 0 && (
                    <Box component='tr'>
                      <Box component='td' p='8px 10px' colSpan={4}>
                        <Typography variant='caption' color='text.secondary'>No orders yet on this brand.</Typography>
                      </Box>
                    </Box>
                  )}
                  {(detail?.recent_orders || []).map((o) => (
                    <Box component='tr' key={o._id} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box component='td' p='8px 10px'>
                        <Typography variant='body2' fontWeight='600'>{o.number || String(o._id).slice(-6)}</Typography>
                        <Typography variant='caption' color='text.secondary'>{fmtDateTime(o.placed_at)}</Typography>
                      </Box>
                      <Box component='td' p='8px 10px'>
                        <StatusChip status={o.status} colors={STATUS_COLORS} />
                      </Box>
                      <Box component='td' p='8px 10px' textAlign='right'>{o.item_count}</Box>
                      <Box component='td' p='8px 10px' textAlign='right'>
                        <Typography variant='body2' fontWeight='700'>
                          {Number(o.grand_total || 0).toFixed(2)} {o.currency}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          {(detail?.customer?.addresses || []).length > 0 && (
            <Box>
              <Typography variant='body2' fontWeight='700' mb='6px'>Saved addresses</Typography>
              <Stack spacing='6px'>
                {(detail?.customer?.addresses || []).map((addr, i) => (
                  <Box key={i} p='10px' border='1px solid' borderColor='divider' borderRadius='10px'>
                    <Typography variant='body2'>
                      {addr.line1}{addr.line2 ? `, ${addr.line2}` : ''}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {[addr.city, addr.region, addr.postal_code].filter(Boolean).join(' ')} {addr.country}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          <FormControl size='small' fullWidth>
            <InputLabel>Status on this brand</InputLabel>
            <Select
              label='Status on this brand'
              value={editing.status}
              onChange={(e) => setEditing({ ...editing, status: e.target.value })}
            >
              <MenuItem value='active'>Active</MenuItem>
              <MenuItem value='invited'>Invited</MenuItem>
              <MenuItem value='blocked'>Blocked</MenuItem>
            </Select>
          </FormControl>
          <Typography variant='caption' color='text.secondary' mt='-8px'>
            Blocking a customer keeps their account alive on other brands — they only lose access here.
          </Typography>

          <Box>
            <Typography variant='body2' fontWeight='600' mb='6px'>Tags (internal)</Typography>
            <Stack direction='row' spacing='6px' flexWrap='wrap' mb='6px'>
              {(editing.tags || []).map((t) => (
                <Chip
                  key={t}
                  label={t}
                  color='secondary'
                  size='small'
                  onDelete={() => rmTag(t)}
                  sx={{ mb: '4px' }}
                />
              ))}
            </Stack>
            <Stack direction='row' spacing='8px'>
              <TextField
                size='small'
                fullWidth
                placeholder='vip, wholesale, …'
                value={tagDraft}
                onChange={(e) => setTagDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
              />
              <Button onClick={addTag} variant='outlined'>Add</Button>
            </Stack>
          </Box>

          <TextField
            label='Private note'
            multiline
            rows={3}
            value={editing.note_internal}
            onChange={(e) => setEditing({ ...editing, note_internal: e.target.value })}
            placeholder='Notes only you can see'
            fullWidth
            size='small'
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '14px 20px' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
